import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Monitor, Plus, Minus } from 'lucide-react';
import { useLanguage } from "@/contexts/LanguageContext";

interface ScreenSelectorProps {
  localScreenCount: number;
  maxDevices: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onSliderChange: (value: number[]) => void;
  isOutdoorLocation?: boolean;
  isMobile?: boolean;
}

export const ScreenSelector = ({
  localScreenCount,
  maxDevices,
  onIncrement,
  onDecrement,
  onSliderChange,
  isOutdoorLocation = false,
  isMobile = false
}: ScreenSelectorProps) => {
  const { t } = useLanguage();

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 space-y-4">
      {/* Header chip */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
          <Monitor className="h-3.5 w-3.5 text-primary" />
        </div>
        <span className="font-semibold text-sm text-foreground">{t("screenSelection")}</span>
      </div>

      {/* Counter row */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{t("screensToBook")}</span>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onDecrement}
            disabled={localScreenCount <= 1}
            className={`${isMobile ? 'h-11 w-11' : 'h-8 w-8'} p-0 rounded-xl border-primary/30 hover:bg-primary/10 hover:border-primary/50 disabled:opacity-30 transition-all`}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>

          <div className="min-w-[3rem] text-center">
            <span className="text-2xl font-bold bg-gradient-to-br from-primary to-primary/70 bg-clip-text text-transparent">
              {localScreenCount}
            </span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={onIncrement}
            disabled={localScreenCount >= maxDevices}
            className={`${isMobile ? 'h-11 w-11' : 'h-8 w-8'} p-0 rounded-xl border-primary/30 hover:bg-primary/10 hover:border-primary/50 disabled:opacity-30 transition-all`}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Slider */}
      <div className="space-y-2 px-1">
        <Slider
          value={[localScreenCount]}
          onValueChange={onSliderChange}
          max={maxDevices}
          min={1}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>1 {t("screen")}</span>
          <span>{maxDevices} {t("screens")}</span>
        </div>
      </div>

      <p className="text-center text-xs text-primary/70 bg-primary/10 px-3 py-1.5 rounded-xl">
        {t("selectExactScreens")}
      </p>
    </div>
  );
};
